import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Modal, Popconfirm, Row, Table, TableColumnsType, TableProps, Typography } from "antd";
import axios from "axios";
import { SearchBar } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { Location } from "@bindeleddet/core/types";
import MobileTable from "components/MobileTable/MobileTable";
import { useViewport } from "@bindeleddet/core/hooks";
import { useEffect, useMemo, useState } from "react";
import CreateOrEditLocation from "./CreateOrEditLocation";

const sortLocationsByName = (locations: Location[]) =>
  [...locations].sort((a, b) => a.name.localeCompare(b.name, "nb", { sensitivity: "base" }));

const AdminLocations = () => {
  const { Title } = Typography;
  const { width } = useViewport();

  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);

  useEffect(() => {
    axios
      .get<Location[]>("/events/locations/")
      .then(response => {
        setLocations(sortLocationsByName(response.data));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const filteredLocations = useMemo(
    () =>
      sortLocationsByName(locations).filter(location => location.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [locations, searchTerm]
  );

  type OnChange = NonNullable<TableProps<Location>["onChange"]>;
  type Sorts = {
    columnKey: string;
    order: "ascend" | "descend" | null;
  };

  const [sortedInfo, setSortedInfo] = useState<Sorts | undefined>(undefined);

  const handleChange: OnChange = (_, __, sorter) => {
    setSortedInfo(sorter as Sorts);
  };

  const handleCreateOrEdit = (location: Location) => {
    const updatedList = selectedLocation
      ? locations.map(existing => (existing.id === location.id ? location : existing))
      : [...locations, location];

    setLocations(sortLocationsByName(updatedList));
    setIsModalOpen(false);
    setSelectedLocation(undefined);
  };

  const columns: TableColumnsType<Location> = [
    {
      title: "Navn",
      dataIndex: "name",
      key: "name",
      sorter: (a: Location, b: Location) => a.name.localeCompare(b.name, "nb", { sensitivity: "base" }),
      sortOrder: sortedInfo?.columnKey === "name" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Kapasitet",
      dataIndex: "capacity",
      key: "capacity",
      sorter: (a: Location, b: Location) => (a.capacity ?? 0) - (b.capacity ?? 0),
      sortOrder: sortedInfo?.columnKey === "capacity" ? sortedInfo.order : null,
    },
    {
      key: "edit",
      render: (_, record) => (
        <EditOutlined
          onClick={() => {
            setSelectedLocation(record);
            setIsModalOpen(true);
          }}
          style={{ fontSize: 20, cursor: "pointer" }}
        />
      ),
      width: 50,
    },
    {
      key: "delete",
      render: (_, record) => (
        <Popconfirm
          title={`Slett "${record.name}"`}
          okText="Slett"
          cancelText="Avbryt"
          onConfirm={() => {
            axios
              .delete(`/events/locations/${record.id}/`)
              .then(() => {
                message("success", "Lokasjon slettet");
                setLocations(locations.filter(location => location.id !== record.id));
              })
              .catch(() => message("error", "Kunne ikke slette lokasjon"));
          }}
        >
          <DeleteOutlined style={{ color: "red", fontSize: 20, cursor: "pointer" }} />
        </Popconfirm>
      ),
      width: 50,
    },
  ];

  return (
    <>
      <Card>
        <Row justify="space-between" align="middle">
          <Title level={2}>Lokasjoner</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedLocation(undefined);
              setIsModalOpen(true);
            }}
          >
            Opprett ny lokasjon
          </Button>
        </Row>
        <Row style={{ margin: "25px auto", width: "100%" }}>
          <SearchBar value={searchTerm} placeholder="Søk..." handleSearch={handleSearch} />
        </Row>
        {width > 768 ? (
          <Table
            columns={columns}
            rowKey="id"
            dataSource={filteredLocations}
            loading={loading}
            onChange={handleChange}
          />
        ) : (
          <MobileTable
            columns={[
              {
                title: "Navn",
                dataIndex: "name",
                key: "name",
              },
              {
                title: "Kapasitet",
                dataIndex: "capacity",
                key: "capacity",
              },
            ]}
            data={filteredLocations}
            loading={loading}
            onClick={id => {
              const location = locations.find(l => l.id === id);
              if (location) {
                setSelectedLocation(location);
                setIsModalOpen(true);
              }
            }}
            emptyText="Ingen lokasjoner funnet"
          />
        )}
      </Card>
      <Modal
        title={<Title level={3}>{selectedLocation ? "Oppdaterer lokasjon" : "Oppretter lokasjon"}</Title>}
        centered
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedLocation(undefined);
        }}
        width="50%"
        footer={null}
      >
        <CreateOrEditLocation
          location={selectedLocation}
          onSuccess={handleCreateOrEdit}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedLocation(undefined);
          }}
        />
      </Modal>
    </>
  );
};

export default AdminLocations;
